import "./OrdersPrintablePage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { Dictionary, groupBy } from "lodash";
import { DateTime } from "luxon";
import QRCode from "qrcode.react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router";

import {
  AdminLabOrderList,
  AdminLabOrder,
  AdminLabOrderPrintLogLabOrderList,
  CaseAdmin,
} from "pwapi";

import { ExternalLabList, StainList } from "custom-models/frontLoader";

import {
  adminLabOrderPrintRetrieveRequest,
  printSlidesLabOrderPrintLog,
  updateLabOrderPrint,
} from "shared/actions/labOrdersActions";
import { useIsLoading, useRequestError } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import {
  dateTimeToLocaleDateTimeString,
  timestampToLocaleDateTimeString,
} from "shared/utils/dates";
import {
  mapReasonCodeToTitle,
  normalizeOrderNotes,
} from "shared/utils/flaggingUtils";
import { batchPrintSlidesLink } from "shared/utils/printUtils";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";
import { getNameString } from "shared/utils/userUtils";

import AppSpinner from "AppSpinner";
import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import LogoLink from "components/header/LogoLink";
import PWLogo from "images/logo.png";
import { useAppDispatch, useAppSelector } from "store/hooks";

enum PriorityWeight {
  "stat" = 3,
  "rush" = 2,
  "normal" = 1,
}

const OrderDetails = ({ printOrder }: { printOrder: AdminLabOrderList }) => {
  const orderLabStain = useAppSelector(
    ({ labStains }) => labStains[printOrder?.lab_stain_id || 0]
  );
  const orderStain = useAppSelector(
    ({ stains }) => stains[orderLabStain?.stain_id || 0]
  );
  const externalStain = useAppSelector(
    ({ externalLabStains }) =>
      externalLabStains[orderLabStain?.external_lab_stain_id || 0]
  );
  const externalLab = useAppSelector(
    ({ externalLabs }) => externalLabs[externalStain?.external_lab_id || 0]
  );
  const stain = orderStain || {
    id: 1,
    name: "H&E",
    abbreviation: "H&E",
    category: "ROUTINE",
  };

  if (!printOrder) return null;

  return <OrderDetailsView {...{ externalLab, printOrder, stain }} />;
};

interface OrderDetailsViewProps {
  externalLab?: ExternalLabList;
  printOrder: AdminLabOrderList;
  stain: StainList;
}
export const OrderDetailsView: React.FC<OrderDetailsViewProps> = ({
  externalLab,
  printOrder,
  stain,
}) => {
  const { flag, note: orderNote } = printOrder;
  const flagReasonCode = flag?.reason;
  const flagNote = flag?.note;
  const orderNotes = normalizeOrderNotes(orderNote, flagNote);

  return (
    <>
      <span
        className={classNames("Order__details", {
          "Order__details--cancelled": !!printOrder.cancelled_at,
        })}
        data-testid="Order__details"
      >
        <div className="Order__flex--column">
          <div className="Order__flex--center">
            <code className="Order__font--black">{stain.name}</code>
            <code className="Order__font--black">({stain.category})</code>
            {printOrder.type === AdminLabOrder.type.RESCAN && (
              <span className="Order__font--red">RESCAN</span>
            )}
            {externalLab && (
              <span className="Order__flex--center">
                <code className="Order__font--grey">Send-Out:</code>
                <code className="Order__font--grey">{externalLab.name}</code>
              </span>
            )}
          </div>
          {!!flagReasonCode && (
            <span className="Order__details--reason">
              <FontAwesomeIcon icon={"flag"} />{" "}
              {mapReasonCodeToTitle(flagReasonCode)}
            </span>
          )}
          {orderNotes.map((note) => (
            <span key={note} className="Order__details--note">
              {note}
            </span>
          ))}
        </div>
        <span className="Order__details--date">
          <span className="Order__details--orderer">
            Ordered:{" "}
            {!!printOrder.user?.id &&
              getNameString(
                printOrder.user.first_name,
                printOrder.user.last_name,
                true
              )}
            {!!printOrder.user?.id && printOrder.created_at && " - "}
            {printOrder.created_at
              ? timestampToLocaleDateTimeString(printOrder.created_at)
              : null}
          </span>
          {printOrder.completed_at ? (
            <>
              Completed:{" "}
              {timestampToLocaleDateTimeString(printOrder.completed_at)}
            </>
          ) : null}
          {printOrder.cancelled_at ? (
            <>
              Cancelled:{" "}
              {timestampToLocaleDateTimeString(printOrder.cancelled_at)}
            </>
          ) : null}
        </span>
      </span>
    </>
  );
};

const OrdersPrintablePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const labPrintId = parseInt(
    useParams<{ print_id: string }>().print_id || "0"
  );
  const canPrintLabOrdersBlockBarcode = useAppSelector((state) =>
    selectFeatureFlag(state, "canPrintLabOrdersBlockBarcode")
  );
  const printSlideFromOrder = useAppSelector((state) =>
    selectFeatureFlag(state, "printSlideFromOrder")
  );

  const currentUser = useAppSelector((state) => state.auth?.user);

  const labPrint = useAppSelector(
    ({ admin }) => admin.labOrderPrintLog[labPrintId]
  );
  const isLabPrintLoading = useIsLoading(`admin/orders/print/${labPrintId}`);
  const isLabPrintError = useRequestError(`admin/orders/print/${labPrintId}`);
  useEffect(() => {
    labPrintId &&
      labPrint?.id !== labPrintId &&
      !isLabPrintLoading &&
      !isLabPrintError &&
      dispatch(adminLabOrderPrintRetrieveRequest({ id: labPrintId }));
  }, [labPrintId, isLabPrintLoading, labPrint?.id, isLabPrintError]);

  const isPrintSlidesLabOrderPrintLogLoading = useIsLoading(
    printSlidesLabOrderPrintLog,
    { id: labPrintId }
  );
  const [reprintSlideLabelsURL, setReprintSlideLabelsURL] = useState<string>();
  const [disablePrintSlidesButton, setDisablePrintSlidesButton] =
    useState(false);

  const handleAfterPrint = (_: Event) => {
    !currentUser?.is_superuser &&
      labPrintId &&
      dispatch(
        updateLabOrderPrint({
          id: labPrintId,
          requestBody: { printed_at: DateTime.now().toISO() },
        })
      );
  };

  useEffect(() => {
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const sortOrders = (labOrders?: AdminLabOrderList[]) => {
    return labOrders
      ?.filter((order) => order.created_at)
      .sort((a, b) => (a.created_at! > b.created_at! ? 1 : -1));
  };

  //Oldest order first
  const sortedOrders = sortOrders(labPrint?.lab_orders);

  // Group the orders by the case accession
  const ordersByCase = groupBy(labPrint?.lab_orders, "case_accession");

  // Sort the grouped orders for case sorting when showing the page
  Object.keys(ordersByCase).forEach((accession) => {
    ordersByCase[accession] = sortOrders(ordersByCase[accession]) || [];
  });

  const sortedCases = Object.keys(ordersByCase).sort(
    (accessionA, accessionB) => {
      const orderA = ordersByCase[accessionA][0];
      const orderB = ordersByCase[accessionB][0];

      // If the priority weight is the same, additional properties need to be checked
      if (
        PriorityWeight[orderA.case_priority as keyof typeof PriorityWeight] ===
        PriorityWeight[orderB.case_priority as keyof typeof PriorityWeight]
      ) {
        // For normal priority, clinic priority comes first
        if (
          orderA.case_priority === CaseAdmin.priority.NORMAL &&
          orderA.clinic_tat !== orderB.clinic_tat
        ) {
          return orderA.clinic_tat ? -1 : 1;
        }

        // Finally if all else is the same, the created_at date is the final sort
        return orderA.created_at! > orderB.created_at! ? 1 : -1;
      } else {
        // If the priority weights are not the same, the higher priority comes first
        return PriorityWeight[
          orderA.case_priority as keyof typeof PriorityWeight
        ] > PriorityWeight[orderB.case_priority as keyof typeof PriorityWeight]
          ? -1
          : 1;
      }
    }
  );

  // Group the orders by case then by block
  const orderByBlock = Object.keys(ordersByCase).reduce(
    (
      acc: { [key: string]: Dictionary<AdminLabOrderPrintLogLabOrderList[]> },
      key
    ) => ({
      ...acc,
      [key]: groupBy(ordersByCase[key], "block_name"),
    }),
    {}
  );

  const getPriorityLabel = (order?: AdminLabOrderList) => {
    if (!order) return null;
    if (order.case_priority !== CaseAdmin.priority.NORMAL) {
      return order.case_priority?.toUpperCase();
    } else {
      return order.clinic_tat ? "PRIORITY" : null;
    }
  };

  return !isLabPrintLoading && labPrint ? (
    <>
      <Helmet>
        <title>{`Orders-${
          labPrint.printed_at || DateTime.now().toUTC().toISO()
        }`}</title>
      </Helmet>
      <div className="OrderPrintablePage--wrap">
        <div className="OrderPrintableBar OrderPrintableBar--header">
          <span
            className="OrderPrintableBar--link"
            onClick={() => navigate(-1)}
          >
            <LogoLink type="simple" />
            Return to Orders list
          </span>
        </div>
        <div className="OrderPrintableBar OrderPrintableBar--footer">
          {printSlideFromOrder && (
            <PathologyWatchButton
              onClick={() => {
                if (reprintSlideLabelsURL) {
                  window.open(reprintSlideLabelsURL);
                } else {
                  dispatch(
                    printSlidesLabOrderPrintLog({ id: labPrintId })
                  ).then((r: { case_id: number; id: number }[]) => {
                    if (r?.length) {
                      const url = batchPrintSlidesLink(
                        [...new Set(r.map((r) => r.case_id))],
                        [...new Set(r.map((r) => r.id))],
                        "zebra"
                      );
                      window.open(url);
                      setReprintSlideLabelsURL(url);
                    } else {
                      setDisablePrintSlidesButton(true);
                    }
                  });
                }
              }}
              color="grey"
              disabled={
                isPrintSlidesLabOrderPrintLogLoading || disablePrintSlidesButton
              }
            >
              <>
                {reprintSlideLabelsURL ? "Reprint" : "Print"} Unprinted Slide
                Labels
              </>
            </PathologyWatchButton>
          )}
          <PathologyWatchButton onClick={() => window.print()} color="green">
            Print Orders
          </PathologyWatchButton>
        </div>
        <div className="OrderPrintablePage">
          <table className="OrderPrintableTable">
            <thead>
              <tr>
                <td>
                  <div className="OrderPrintableHeader">
                    <img
                      className="brand-img"
                      src={PWLogo}
                      alt="Pathologywatch Logo"
                      height="40px"
                    />
                    <span style={{ textAlign: "right" }}>
                      {labPrint.printed_by && (
                        <>
                          {getNameString(
                            labPrint.printed_by.first_name,
                            labPrint.printed_by.last_name,
                            true
                          )}
                          <br />
                        </>
                      )}
                      {labPrint.printed_at
                        ? timestampToLocaleDateTimeString(labPrint.printed_at)
                        : dateTimeToLocaleDateTimeString(DateTime.now())}
                    </span>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="OrderPrintableTable__metrics">
                    <span>
                      Order Period:{" "}
                      {labPrint.filter_criteria?.created_at_after
                        ? timestampToLocaleDateTimeString(
                            labPrint.filter_criteria?.created_at_after
                          )
                        : sortedOrders?.[0].created_at &&
                          timestampToLocaleDateTimeString(
                            sortedOrders[0].created_at!
                          )}
                      {" - "}
                      {timestampToLocaleDateTimeString(
                        labPrint.filter_criteria?.created_at_before ||
                          sortedOrders?.[sortedOrders.length - 1].created_at ||
                          DateTime.now().toISO
                      )}
                    </span>
                  </div>
                </td>
              </tr>
              {labPrint &&
                sortedCases.map((caseAccession) => {
                  const patient = ordersByCase[caseAccession].at(0)?.patient;
                  const priorityLabel = getPriorityLabel(
                    ordersByCase[caseAccession].at(0)
                  );
                  return (
                    <tr
                      key={caseAccession}
                      style={
                        printSlideFromOrder
                          ? {
                              pageBreakAfter: "always",
                            }
                          : undefined
                      }
                    >
                      <td>
                        <div className="Order">
                          <div className="Case__details">
                            {priorityLabel && (
                              <code
                                className={classNames(
                                  "Case__details--priority",
                                  {
                                    "Priority--rush": priorityLabel === "RUSH",
                                    "Priority--stat": priorityLabel === "STAT",
                                    "Priority--tat":
                                      priorityLabel === "PRIORITY",
                                  }
                                )}
                              >
                                {priorityLabel}
                              </code>
                            )}
                            <strong>{caseAccession}</strong>
                            {getNameString(
                              patient?.first_name,
                              patient?.last_name
                            )}{" "}
                            <em>
                              ({patient?.sex} &mdash;{" "}
                              {patient?.date_of_birth &&
                                DateTime.fromFormat(
                                  patient.date_of_birth,
                                  "yyyy-MM-dd"
                                ).toFormat("MM/dd/yyyy")}
                              )
                            </em>
                          </div>
                          <div>
                            {Object.entries(orderByBlock[caseAccession]).map(
                              ([blockName, orders]) => (
                                <article className="Order__block">
                                  <strong className="Block__details--name">
                                    {blockName} &mdash;{" "}
                                    {[
                                      orders.at(0)?.specimen?.type,
                                      orders
                                        .at(0)
                                        ?.specimen?.site?.toUpperCase(),
                                      procedureEnumToLabel(
                                        orders.at(0)?.specimen?.procedure
                                      ),
                                    ]
                                      .filter((v) => v)
                                      .join(", ")}
                                  </strong>
                                  <div
                                    className="Block__details"
                                    key={blockName}
                                  >
                                    <div>
                                      {canPrintLabOrdersBlockBarcode
                                        ? [
                                            ...new Set(
                                              orders
                                                .map((o) => o.block_id)
                                                .filter((v) => v)
                                            ),
                                            // Noting that this should only ever be "one", but because of the
                                            // groupBy block_name, it could be more than one.
                                          ].map((block_id) => (
                                            <figure
                                              className="Block__details__barcode"
                                              key={block_id}
                                              title={`block-${block_id}`}
                                              role="figure"
                                            >
                                              <QRCode
                                                value={`block-${block_id}`}
                                                size={38}
                                                bgColor={"#ffffff"}
                                                fgColor={"var(--black)"}
                                                level={"Q"}
                                                includeMargin={false}
                                                renderAs={"svg"}
                                              />
                                            </figure>
                                          ))
                                        : null}
                                    </div>
                                    <div className="Block__details--border">
                                      {sortOrders(orders)?.map((order) => (
                                        <OrderDetails
                                          printOrder={order}
                                          key={order.id}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                </article>
                              )
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  ) : (
    <AppSpinner>Loading printable orders...</AppSpinner>
  );
};

export default OrdersPrintablePage;
